<script lang="ts">
  import { REST_URL } from '../utils/config';
  let {
    repositoryName,
    ownerUsername,
  }: { repositoryName: string; ownerUsername: string } = $props();

  // Construct repository URL (adjust based on your setup)
  const repoUrl = `${REST_URL}/${ownerUsername}/${repositoryName}.git`;
  const httpsUrl = `https://github.com/${ownerUsername}/${repositoryName}.git`; // Update with your actual domain
</script>

<div
  class="border border-gray-300 dark:border-gray-700 rounded-lg p-8 my-6 bg-gray-50 dark:bg-gray-800"
>
  <div class="max-w-4xl">
    <h2 class="text-xl font-semibold mb-6 text-gray-900 dark:text-gray-100">
      Quick setup — if you've done this kind of thing before
    </h2>

    <!-- HTTPS/SSH URLs -->
    <div class="mb-6">
      <div class="flex items-center space-x-4 mb-2">
        <button
          class="px-3 py-1 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          HTTPS
        </button>
        <button
          class="px-3 py-1 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
        >
          SSH
        </button>
      </div>
      <div
        class="flex items-center bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded px-3 py-2"
      >
        <code class="flex-1 text-sm text-gray-800 dark:text-gray-200 font-mono"
          >{repoUrl}</code
        >
        <button
          class="ml-2 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
          onclick={() => navigator.clipboard.writeText(repoUrl)}
          title="Copy to clipboard"
        >
          <svg
            class="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
        </button>
      </div>
    </div>

    <div class="border-t border-gray-300 dark:border-gray-700 pt-6 mb-6"></div>

    <!-- Create a new repository -->
    <div class="mb-8">
      <h3 class="text-lg font-semibold mb-3 text-gray-900 dark:text-gray-100">
        …or create a new repository on the command line
      </h3>
      <div
        class="bg-gray-900 dark:bg-black rounded-lg p-4 font-mono text-sm text-gray-100 overflow-x-auto"
      >
        <div class="flex items-start">
          <pre class="flex-1"><code
              >echo "# {repositoryName}" >> README.md
				git init
				git add README.md
				git commit -m "first commit"
				git branch -M main
				git remote add origin {repoUrl}
				git push -u origin main</code
            ></pre>
          <button
            class="ml-2 p-2 hover:bg-gray-700 rounded flex-shrink-0"
            onclick={() =>
              navigator.clipboard.writeText(
                `echo "# ${repositoryName}" >> README.md\ngit init\ngit add README.md\ngit commit -m "first commit"\ngit branch -M main\ngit remote add origin ${repoUrl}\ngit push -u origin main`,
              )}
            title="Copy to clipboard"
          >
            <svg
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Push existing repository -->
    <div class="mb-8">
      <h3 class="text-lg font-semibold mb-3 text-gray-900 dark:text-gray-100">
        …or push an existing repository from the command line
      </h3>
      <div
        class="bg-gray-900 dark:bg-black rounded-lg p-4 font-mono text-sm text-gray-100 overflow-x-auto"
      >
        <div class="flex items-start">
          <pre class="flex-1"><code
              >git remote add origin {repoUrl}
git branch -M main
git push -u origin main</code
            ></pre>
          <button
            class="ml-2 p-2 hover:bg-gray-700 rounded flex-shrink-0"
            onclick={() =>
              navigator.clipboard.writeText(
                `git remote add origin ${repoUrl}\ngit branch -M main\ngit push -u origin main`,
              )}
            title="Copy to clipboard"
          >
            <svg
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Import from another repository -->
    <div class="mb-4">
      <h3 class="text-lg font-semibold mb-3 text-gray-900 dark:text-gray-100">
        …or import code from another repository
      </h3>
      <p class="text-sm text-gray-600 dark:text-gray-400 mb-2">
        You can initialize this repository with code from a Subversion,
        Mercurial, or TFS project.
      </p>
      <button
        class="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded font-medium text-sm"
      >
        Import code
      </button>
    </div>
  </div>
</div>
